import React, { useRef, useEffect, useState } from 'react';
import { Group, Rect, Circle, Text, Image as KonvaImage, Label, Tag } from 'react-konva';
import type { PartyMember, NPC, POI, RenderedImageRect } from '../../types/types';
import useImage from 'use-image';
import Konva from 'konva';
import { useStableCallback } from '../../hooks/hookUtils';

export type MarkerItem = (PartyMember | NPC | POI) & { itemType: 'player' | 'npc' | 'poi' };

interface DraggableKonvaMarkerProps {
  item: MarkerItem;
  imageRect: RenderedImageRect;
  id: number;
  x: number;
  imageUrl: string;
  isSelected: boolean;
  isHighlighted: boolean;
  onUpdateMarkerPosition: (type: 'player' | 'npc' | 'poi', id: number, x: number, y: number) => void;
  onSetSelectedMarker: (markerId: number | null) => void;
}

const DraggableKonvaMarker: React.FC<DraggableKonvaMarkerProps> = ({
  item, imageRect, imageUrl, isSelected, isHighlighted, onUpdateMarkerPosition, onSetSelectedMarker,
}) => {
  const [markerImage] = useImage(imageUrl, 'anonymous');
  const groupRef = useRef<Konva.Group>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const markerSize = 32;
  // All marker types (player, npc, poi) use posX and posY for their coordinates.
  const itemX = (item as PartyMember | NPC | POI).posX;
  const itemY = (item as PartyMember | NPC | POI).posY;

  const position = {
    x: imageRect.x + imageRect.width * ((Number(itemX) || 50) / 100), // Convert Decimal to number, with a fallback
    y: imageRect.y + imageRect.height * ((Number(itemY) || 50) / 100), // Convert Decimal to number, with a fallback
  };

  // Animation effect for highlighted markers (e.g., when selected from the sidebar).
  // Creates a "pop" effect by scaling up then bouncing back with elastic easing.
  // Note: Tween cleanup is not strictly required as Konva auto-cleans tweens when they complete,
  // but we rely on the isHighlighted guard to prevent re-triggering.
  useEffect(() => {
    if (isHighlighted && groupRef.current) {
      const node = groupRef.current;
      const originalScale = node.scaleX();
      const tween = new Konva.Tween({
        node: node,
        scaleX: originalScale * 1.5,
        scaleY: originalScale * 1.5,
        duration: 0.3,
        easing: Konva.Easings.EaseOut,
        onFinish: () => {
          new Konva.Tween({
            node: node,
            scaleX: originalScale,
            scaleY: originalScale,
            duration: 0.5,
            easing: Konva.Easings.ElasticEaseOut,
          }).play();
        }
      });
      tween.play();
    }
  }, [isHighlighted, item.id]);

  // Stable callback for drag end - prevents recreating function on every render.
  // Uses useStableCallback to always have access to latest imageRect and item values.
  const handleDragEnd = useStableCallback((e: Konva.KonvaEventObject<DragEvent>) => {
    const newX = e.target.x();
    const newY = e.target.y();

    // Convert absolute Konva coordinates back to percentages relative to the image
    const newXPercent = ((newX - imageRect.x) / imageRect.width) * 100;
    const newYPercent = ((newY - imageRect.y) / imageRect.height) * 100;

    onUpdateMarkerPosition(item.itemType, item.id, newXPercent, newYPercent);
  });

  // Stable callback for click - toggles marker selection.
  // Internalized from parent to avoid creating new function references per marker.
  const handleClick = useStableCallback(() => {
    onSetSelectedMarker(isSelected ? null : item.id);
  });

  return (
    <Group
      ref={groupRef}
      x={position.x}
      y={position.y}
      draggable
      onDragEnd={handleDragEnd}
      offset={{ x: markerSize / 2, y: markerSize / 2 }}
      onClick={handleClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      id={item.id.toString()}
    >
      {/* Background shape */}
      {item.itemType === 'player' ? (
        <Rect
          width={markerSize}
          height={markerSize}
          fill={(item as PartyMember).color ?? '#ccc'}
          stroke={isSelected ? '#00ffff' : 'white'}
          strokeWidth={2}
          cornerRadius={6}
          shadowColor="black"
          shadowBlur={8}
          shadowOpacity={0.7}
        />
      ) : (
        <Circle
          radius={markerSize / 2}
          fill={(item as PartyMember).color ?? '#ccc'}
          stroke={isSelected ? '#00ffff' : 'white'}
          strokeWidth={2}
          shadowColor="black"
          shadowBlur={8}
          shadowOpacity={0.7}
        />
      )}
      {/* Image or Initial */}
      {markerImage ? (
        <KonvaImage image={markerImage} width={markerSize} height={markerSize} cornerRadius={item.itemType === 'player' ? 6 : 0} />
      ) : (
        <Text
          text={item.name.charAt(0)}
          width={markerSize}
          height={markerSize}
          align="center"
          verticalAlign="middle"
          fontSize={18}
          fontStyle="bold"
          fill="white"
        />
      )}
      {/* Tooltip */}
      {showTooltip && (
        <Label
          // Position the tooltip above the marker
          y={-10}
          offsetY={markerSize / 2}
        >
          <Tag
            fill="black"
            pointerDirection="down"
            pointerWidth={10}
            pointerHeight={10}
            lineJoin="round"
            shadowColor="black"
            shadowBlur={10}
            shadowOpacity={0.5}
          />
          <Text text={item.name} fontFamily="Calibri" fontSize={18} padding={5} fill="white" />
        </Label>
      )}
    </Group>
  );
};

interface KonvaMarkersLayerProps {
  imageRect: RenderedImageRect;
  markers: MarkerItem[];
  imageMap: Map<number, string>;
  selectedMarkerId: number | null | undefined;
  highlightedMarkerId: number | null | undefined;
  onUpdateMarkerPosition: (type: 'player' | 'npc' | 'poi', id: number, x: number, y: number) => void;
  onSetSelectedMarker: (markerId: number | null) => void;
}

const KonvaMarkersLayer: React.FC<KonvaMarkersLayerProps> = ({
  imageRect, markers, imageMap, selectedMarkerId, highlightedMarkerId,
  onUpdateMarkerPosition, onSetSelectedMarker,
}) => {
  return (
    <Group>
      {(markers ?? []).map(item => {
        const imageUrl = (item.imageId ? imageMap.get(item.imageId) : '') ?? '';
        const isSelected = selectedMarkerId === item.id;
        return item.id && <DraggableKonvaMarker
          key={`${item.itemType}-${item.id}`}
          item={item}
          imageRect={imageRect}
          id={item.id}
          x={0} // Position is calculated inside DraggableKonvaMarker
          imageUrl={imageUrl}
          isSelected={isSelected}
          isHighlighted={highlightedMarkerId === item.id}
          onUpdateMarkerPosition={onUpdateMarkerPosition}
          onSetSelectedMarker={onSetSelectedMarker}
        />;
      })}
    </Group>
  );
};

export default KonvaMarkersLayer;
